import React, { Component } from 'react';

import InsightCardItem from './InsightCardItem';

import './InsightCard.scss';

import { ANALYSIS_STATUS } from '../../../services/analysis';
import { getWorkMinutesPerDay } from '../../../util/dates';
import { trackDashboardAlert } from '../../../util/mixpanel';

const STATUS_TITLE = {
  [ANALYSIS_STATUS.NORMAL]: 'stable',
  [ANALYSIS_STATUS.NORMAL_RARE]: 'stable',
  [ANALYSIS_STATUS.AT_RISK]: 'at risk',
  [ANALYSIS_STATUS.LOW]: 'unstable',
  [ANALYSIS_STATUS.LOW_RARE]: 'unstable',
  [ANALYSIS_STATUS.HIGH_RARE]: 'unstable',
  [ANALYSIS_STATUS.HIGH]: 'unstable',
  [ANALYSIS_STATUS.NOT_ENOUGH_DATA]: 'no data'
};

const CHART_NAMES = {
  'ctsp': 'Cycle Time Scatterplot',
  'cta': 'Aging Chart',
  'ctb': 'Cycle Time Breakdown Chart',
  'trc': 'Throughput Run Chart',
  'fe': 'Flow Efficiency Chart'
};

const ANALYSIS_TEXTS = {
  ct: {
    [ANALYSIS_STATUS.NORMAL]: {
      text: 'Your cycle time is consistent and well-managed! For example, the cycle time of your most recently completed TASK_STRING <b>LAST_COMPLETED_ITEM_NAME</b> is on track, currently at <b>LAST_COMPLETED_ITEM_CYCLE_TIME</b>, staying well within the <b>upper UNPL_VALUE-day limit</b> that aligns with your process. Great work!',
      description: "Your cycle time is stable! If you're still looking for improvement opportunities, consider reviewing the items with the longest cycle time and identify what slowed them down:"
    },
    [ANALYSIS_STATUS.LOW]: {
      text: 'You have finished your work significantly faster than usual with <b>cycle time SIGNAL_CYCLE_TIME days</b>, which is <b>below the LNPL_VALUE days threshold</b>. It suggests that your recent process improvements are highly effective, or it may mean that the TASKS_STRING being completed are simpler or less complex.',
      description: 'Your cycle time has dropped unexpectedly! Review these TASKS_STRING to identify what factors caused this shift and assess their impact:'
    },
    [ANALYSIS_STATUS.HIGH]: {
      text: 'Your cycle time is significantly high at <b>SIGNAL_CYCLE_TIME days</b>, which is <b>DEVIATION_PERCENT_FROM_UNPL above the upper threshold of THRESHOLD_CYCLE_TIME days</b>. High cycle time results in substantial delivery delays. It’s important to take action to meet your commitments and hit your targets!',
      description:
        'Your cycle time is too high! Review the TASKS_STRING with the longest cycle time to identify opportunities for improvement and avoid further delays:'
    },
    [ANALYSIS_STATUS.NOT_ENOUGH_DATA]: {
      text: 'Not enough data to generate insights!',
      description: 'Make sure to complete <b>at least 4 TASKS_STRING</b> to unlock actionable insights!'
    }
  },
  ctb: {
    [ANALYSIS_STATUS.NORMAL]: {
      text: 'Your cycle time for each process step is steady and on track! For instance, for your most recently finished TASK_STRING <b>LAST_COMPLETED_ITEM_NAME</b>, the cycle time in <b>LAST_COMPLETE_ITEM_LONGEST_STATUS_NAME</b> is <b>LAST_COMPLETE_ITEM_LONGEST_STATUS_CYCLE_TIME days</b>, well within your <b>upper UNPL_VALUE-day limit</b>. Excellent job!',
      description: 'Your cycle time is stable! If you’re looking to make further improvements, consider reviewing the TASKS_STRING that spent the most time in <b>LAST_COMPLETE_ITEM_LONGEST_STATUS_NAME</b> and identify what held them back:'
    },
    [ANALYSIS_STATUS.LOW]: {
      text: 'Your <b>SIGNAL_STATUS</b> cycle time is currently 15% below the <b>minimum benchmark of LNPL_VALUE days</b>, indicating that your team is effectively moving TASKS_STRING through this step. Just make sure that this shift doesn’t come at the expense of the quality of your work.',
      description: 'Your <b>SIGNAL_STATUS</b> cycle time has dropped unexpectedly! Review the following TASKS_STRING to determine what triggered this quick turnaround and assess its impact:'
    },
    [ANALYSIS_STATUS.HIGH]: {
      text: '<b>SIGNAL_STATUS</b> cycle time is <b>DEVIATION_PERCENT_FROM_UNPL</b> above the upper threshold, pointing at <b>SIGNAL_CYCLE_TIME days</b>. When a specific cycle time deviates significantly, it disrupts your workflow and makes it difficult to provide reliable delivery forecasts!',
      description: 'Your <b>SIGNAL_STATUS</b> cycle time is too high! Focus on assessing the issue that caused the delay to bring your cycle time on that process step back in line:'
    },
    [ANALYSIS_STATUS.NOT_ENOUGH_DATA]: {
      text: 'Not enough data to generate insights!',
      description: 'Make sure to complete <b>at least 4 TASKS_STRING</b> to unlock actionable insights!'
    }
  },
  th: {
    [ANALYSIS_STATUS.NORMAL]: { //done
      text: 'Your throughput is holding steady <b>at DAILY_THROUGHPUT TASKS_STRING per day</b>, staying well within your <b>upper benchmark of UNPL_VALUE TASKS_STRING per day</b>. It seems that your current workload matches your team’s capacity. Great job maintaining this balance!',
      description: "Your throughput is stable! If improving your delivery rate is your goal, review your current work in progress and find out what's holding up the TASKS_STRING that are just about done:"
    },
    [ANALYSIS_STATUS.LOW]: { //done
      text: "Your throughput on <b>SIGNAL_DATE</b> was lower than usual at <b>SIGNAL_THROUGHPUT TASKS_STRING</b>, falling below the established <b>lower benchmark of LNPL_VALUE TASKS_STRING per day</b>. Your team delivered significantly fewer TASKS_STRING than expected at that time. It's important to review what happened to prevent future drops!",
      description: 'Your throughput is too low! To identify the source of delay, review the TASKS_STRING that were just about done on <b>SIGNAL_DATE</b> and see what might have held them up:'
    },
    [ANALYSIS_STATUS.HIGH]: { //done
      text: 'Your throughput has increased to <b>SIGNAL_THROUGHPUT TASKS_STRING on SIGNAL_DATE</b>, going beyond your upper limit of <b>UNPL_VALUE TASKS_STRING</b> per day. While your team may be performing exceptionally well, an unexpectedly high throughput could also mean they are working overtime and pushing themselves beyond their limits to deliver on their commitments.',
      description: 'Your throughput has spiked unexpectedly! Review the following completed TASKS_STRING on <b>SIGNAL_DATE</b> to identify what factors contributed to this boost:'
    },
    [ANALYSIS_STATUS.NORMAL_RARE]: { //done
      //text: 'Your daily completion rate is holding steady <b>​​at DAILY_COMPLETION_RATE</b>, staying well within your <b>upper benchmark of UNPL_VALUE</b>. It seems your workload is well-aligned with your team’s capacity. Excellent performance!',
      text: 'While your throughput is currently <b>at DAILY_THROUGHPUT TASKS_STRING per day</b>, it has remained stable, staying within your <b>upper benchmark of UNPL_VALUE per day</b>. It seems that your current workload matches your team’s capacity. Worth keeping an eye on to ensure this continues to reflect your delivery goals!',
      description: "Your daily completion rate is stable! To further improve, review your current work in progress and find out what's holding up the TASKS_STRING that are just about done:"
    },
    [ANALYSIS_STATUS.HIGH_RARE]: { //done
      text: 'On <b>SIGNAL_DATE</b> TASKS_STRING were being finished sooner than they normally would, with a daily completion rate reaching <b>SIGNAL_RATE</b>, well above the upper <b>benchmark of UNPL_VALUE</b>. While your team may be performing exceptionally well, an unexpectedly high completion rate could also mean they are working overtime and pushing themselves beyond their limits to deliver on their commitments.',
      description: 'Your daily completion rate has spiked unexpectedly! Review the following completed TASKS_STRING on <b>SIGNAL_DATE</b> to identify what factors contributed to this boost:'
    },
    [ANALYSIS_STATUS.LOW_RARE]: { //done
      text: "Your daily completion rate on <b>SIGNAL_DATE</b> was lower than usual at <b>SIGNAL_RATE</b>, falling below the established <b>lower benchmark of LNPL_VALUE</b>. Your team delivered significantly fewer TASKS_STRING than expected at that time. It's important to review what happened to prevent future drops!",
      description: 'Your daily completion rate is too low! To identify the source of delay, review the TASKS_STRING that were just about done on <b>SIGNAL_DATE</b> and see what might have held them up:'
    },
    [ANALYSIS_STATUS.NOT_ENOUGH_DATA]: {
      text: 'Not enough data to generate insights!',
      description: 'Make sure to complete <b>at least 4 TASKS_STRING</b> to unlock actionable insights!'
    }
  },
  fe: {
    [ANALYSIS_STATUS.NORMAL]: {
      text: 'Your flow efficiency is on track! For example, your latest completed TASK_STRING <b>LAST_COMPLETED_ITEM_NAME</b> is at <b>LAST_COMPLETED_ITEM_FE%</b>, which is within the <b>expected range of LNPL_VALUE to UNPL_VALUE</b>. The balance between work time and wait time is well-maintained. Well done!',
      description: 'Your flow efficiency is stable! If you want to further reduce wait times, consider analyzing the following items with the lowest flow efficiency and highest cycle time:'
    },
    [ANALYSIS_STATUS.LOW]: {
      text: 'Flow efficiency has dropped to <b>SIGNAL_FE%</b>, below the <b>minimum threshold of LNPL_VALUE%</b>. This means that TASK_STRING are spending significant time waiting rather than being actively worked on!',
      description: 'Your flow efficiency is too low! Analyze the following TASKS_STRING to identify the cause of delay:'
    },
    [ANALYSIS_STATUS.HIGH]: {
      text: 'Your flow efficiency has <b>increased to SIGNAL_FE%</b>, which is well above your <b>upper benchmark of UNPL_VALUE%</b>. While the team is effectively minimizing wait times and maximizing work time, an unexpectedly high flow efficiency could signal an increase in work in progress across your process steps, which can become challenging when there aren’t additional people to manage the workload!',
      description: 'Your flow efficiency has spiked unexpectedly! Review the following TASKS_STRING to analyze any recent changes in your workflow and ensure the workload remains manageable:'
    },
    [ANALYSIS_STATUS.NOT_ENOUGH_DATA]: {
      text: 'Not enough data to generate insights!',
      description: 'Make sure to complete <b>at least 4 TASKS_STRING</b> to unlock actionable insights!'
    }
  },
  wip: {
    [ANALYSIS_STATUS.NORMAL]: {
      /*text: 'Your WIP count is holding steady at <b>CURRENT_WIP TASKS_STRING</b>, staying well below the <b>upper benchmark of UNPL_VALUE</b>. It looks like your team is handling the workload effectively and may even be ready to take on more!',
      description: 'Your WIP is stable! Prioritize the next backlog TASKS_STRING, and get them ready for your team to start working on:'*/
      text: 'Your WIP count is holding steady at <b>CURRENT_WIP TASKS_STRING</b>, staying well below the <b>upper benchmark of UNPL_VALUE</b>. It looks like your team is handling the workload effectively. Keep up the momentum and continue focusing on reducing WIP!',
      description: 'Your WIP is stable! Focus on moving forward with your current TASKS_STRING, especially the ones that are nearly finished:'
    },
    [ANALYSIS_STATUS.LOW]: {
      /*text: 'Your WIP count is low at <b>CURRENT_WIP TASKS_STRING<b>, even below the minimum benchmark of <b>LNPL_VALUE TASKS_STRING</b>. Your team has capacity to take on more work! While a bit of idle time can help reduce stress and boost quality, letting your process stay underutilized for too long will slow you down.',
      description: 'Your WIP has dropped significantly! Quickly prioritize the next backlog TASKS_STRING, and get them ready for your team to start working on:'*/
      text: 'Your WIP count is low at <b>CURRENT_WIP TASKS_STRING<b>, below the minimum benchmark of <b>LNPL_VALUE TASKS_STRING</b>. Your team is making solid progress in limiting work in progress, but staying below the thresholds is making your process less predictable. If your intent is to reduce WIP long-term, maintaining this pace will help your thresholds adjust accordingly and bring your process back to a stable state.',
      description: 'Your WIP has dropped significantly. If your goal is to reduce WIP, keep this pace and your thresholds will adjust accordingly. To drive things forward, focus on:'
    },
    [ANALYSIS_STATUS.HIGH]: {
      text: "Your WIP count is currently exceeding the upper benchmark of your process, <b>sitting at CURRENT_WIP TASKS_STRING</b> compared to the <b>limit of UNPL_VALUE</b>. You're starting more work than you're finishing, which is actively slowing down your delivery speed.",
      description: 'Your WIP is too high! Focus on moving current TASKS_STRING along, especially the ones that are almost done:'
    },
    [ANALYSIS_STATUS.AT_RISK]: {
      text: "Your WIP count is currently at its limit, with a total of <b>CURRENT_WIP TASKS_STRING</b>, reaching your upper benchmark. This means you're operating at full capacity, and any additional task you take on will slow you down!",
      description: 'Your WIP is at risk! Don’t start any new work until something goes out of your process! Focus on moving forward with your current TASKS_STRING, especially the ones that are nearly finished:'
    },
    [ANALYSIS_STATUS.NOT_ENOUGH_DATA]: {
      text: 'Not enough data to generate insights!',
      description: 'Make sure to complete <b>at least 4 TASKS_STRING</b> to unlock actionable insights!'
    }
  },
  wipAge: {
    [ANALYSIS_STATUS.NORMAL]: {
      text: 'Your total WIP age is stable at <b>TOTAL_WIP_AGE days</b>, well within the <b>UNPL_VALUE-day upper threshold</b>. Your workflow is running well, with TASKS_STRING moving through your process without delays. Well done!',
      description: 'Your WIP age is stable! Keep up the good work by making sure the following TASKS_STRING continue to follow this consistent pattern:'
    },
    [ANALYSIS_STATUS.LOW]: {
      text: "Your total WIP age is unusually low at <b>just TOTAL_WIP_AGE days</b>, below the minimum <b>benchmark of LNPL_VALUE days</b>. While a low WIP age indicates high efficiency, it could also mean TASKS_STRING are moving through the process too quickly, so make sure that speed doesn't impact the quality of your work.",
      description: 'Your WIP age has dropped significantly! Review the following TASKS_STRING to make sure you keep a good balance between speed and attention to detail:'
    },
    [ANALYSIS_STATUS.HIGH]: {
      text: 'Your total WIP age has reached a critical point, with TASKS_STRING in progress now adding up to over <b>TOTAL_WIP_AGE days</b>, well beyond the <b>upper UNPL_VALUE-day threshold</b>. Take action now to prevent further delays!',
      description: 'Your WIP age is too high! Prioritize the TASKS_STRING with the highest work in progress age to bring your workflow back under control:'
    },
    [ANALYSIS_STATUS.AT_RISK]: {
      text: 'Your total WIP age is increasing significantly, with several TASKS_STRING in progress for <b>nearly TOTAL_WIP_AGE days</b> approaching the threshold of <b>UNPL_VALUE days</b>. If nothing finishes soon, your process could become unmanageable!',
      description: 'Your WIP age is at risk! Consider prioritizing the TASKS_STRING with the highest work in progress age to bring your workflow back under control:'
    },
    [ANALYSIS_STATUS.NOT_ENOUGH_DATA]: {
      text: 'Not enough data to generate insights!',
      description: 'Make sure to complete <b>at least 4 TASKS_STRING</b> to unlock actionable insights!'
    }
  }
};

const ALERT_TYPES = {
  ct: 'process_cycle_time',
  wipAge: 'wip_age',
  wip: 'wip',
  th: 'throughput',
  ctb: 'state_cycle_time',
  fe: 'flow_efficiency'
};

const platformValues = {
  'jira': {
    'TASKS_STRING': 'work items',
    'TASK_STRING': 'work item'
  },
  'asana': {
    'TASKS_STRING': 'tasks',
    'TASK_STRING': 'task'
  },
  'azure': {
    'TASKS_STRING': 'work items',
    'TASK_STRING': 'work item'
  },
  'file': {
    'TASKS_STRING': 'items',
    'TASK_STRING': 'item'
  },
  'trello': {
    'TASKS_STRING': 'cards',
    'TASK_STRING': 'card'
  },
}

const platformReplacement = (app, text) => {
  let result = text;
  if (app && result) {
    _.forEach(platformValues[app], (v, k) => {
      result = result.replace(new RegExp(k, 'gmi'), v);
    });
  }
  return result;
}

const getPercent = (value, limit) => {
  if (value && limit) {
    return Math.abs(Math.round(100 * (value - limit) / limit)).toString() + '%';
  } else return '';
}

const replacementFunc = (app, analysis, data, text) => {
  let result = platformReplacement(app, text);

  const replacements = {
    'DAILY_COMPLETION_RATE': data.displayValue,
    'TOTAL_WIP_AGE': data.displayValue,
    'DAILY_THROUGHPUT': data.displayValue,
    'CURRENT_WIP': data.displayValue,
    'LAST_COMPLETED_ITEM_NAME': data.displayName,
    'LAST_COMPLETE_ITEM_LONGEST_STATUS_NAME': data.displayMetric,
    'LAST_COMPLETE_ITEM_LONGEST_STATUS_CYCLE_TIME': data.displayValue + (data.displayValue == 1 ? ' day' : ' days'),
    'LNPL_VALUE': data.lowerLimit,
    'UNPL_VALUE': data.upperLimit,
    'SIGNAL_DATE': data.displayName,
    'SIGNAL_CARD': data.displayName,
    'SIGNAL_FE': data.displayValue,
    'SIGNAL_THROUGHPUT': data.displayValue,
    'SIGNAL_RATE': data.displayValue,
    'SIGNAL_STATUS': data.displayName,
    'SIGNAL_CYCLE_TIME': data.displayValue,
    'THRESHOLD_CYCLE_TIME': data.upperLimit,
    'STATUS_STRING': data.displayMetric,
    'LAST_COMPLETED_ITEM_NAME': data.displayName,
    'LAST_COMPLETED_ITEM_FE': data.displayValue,
    'LAST_COMPLETED_ITEM_CYCLE_TIME': data.displayValue + (data.displayValue == 1 ? ' day' : ' days'),
    'DEVIATION_PERCENT_FROM_UNPL': getPercent(data.displayValue, data.upperLimit),
    'DEVIATION_PERCENT_FROM_LNPL': getPercent(data.displayValue, data.lowerLimit),
  };

  _.forEach(replacements, (v, k) => {
    result = result.replace(new RegExp(k, 'igm'), v);
  });

  result = result.split(new RegExp('<b>|</b>', 'igm'));
  result = _(result).map((v, k) => (
    k % 2 ? [<strong key={k}>{v}</strong>] : [v]
  )).flatten().value();

  return result;
}

class InsightCard extends Component {
  constructor(props) {
    super(props);
    this.gridRef = React.createRef();
    this.grid = null;
  }

  trackTaskOpened(dashboardName, userSubscription, card, analysis, analysisType) {
    const cardDisplayValue = _.split(card.displayMetric, ' ');

    trackDashboardAlert({
      subscription: userSubscription,
      name: dashboardName,
      alert_type: ALERT_TYPES[analysisType],
      upper_limit: analysis.upperLimit,
      lower_limit: analysis.lowerLimit,
      dashboard_status: analysis.status,
      task_affected: card.card.name,
      metric_value: cardDisplayValue.length > 1 ? +cardDisplayValue[0] : +cardDisplayValue
    });
  }

  getText(analysis, data) {
    if (!data || !data.status || !ANALYSIS_TEXTS[analysis] || !ANALYSIS_TEXTS[analysis][data.status]) {
      return '';
    }
    return replacementFunc(this.props.app, analysis, data, ANALYSIS_TEXTS[analysis][data.status].text);
  }

  getDescription(analysis, data) {
    if (!data || !data.status || !ANALYSIS_TEXTS[analysis] || !ANALYSIS_TEXTS[analysis][data.status]) {
      return '';
    }
    return replacementFunc(this.props.app, analysis, data, ANALYSIS_TEXTS[analysis][data.status].description);
  }

  getTasksLinks(cards, analysisType, analysis) {
    let links = analysis.status !== ANALYSIS_STATUS.NOT_ENOUGH_DATA ?
     _.map(cards, card => card && card.card ? (
      <div className='card-items-link' key={`${card.card.id}-${analysisType}`}>
        <span>{card.displayMetric}</span>
        {card.displayStatus ? (<span>{card.displayStatus}</span>) : undefined}
        <a
          href={card.card.url}
          key={`${card.card.id}-${analysisType}-link`}
          onClick={() => this.trackTaskOpened(this.props.dashboardName, this.props.userSubscription, card, analysis, analysisType)}
          onAuxClick={() => this.trackTaskOpened(this.props.dashboardName, this.props.userSubscription, card, analysis, analysisType)}
          target="_blank">
          {card.card.name}
        </a></div>) : ''
    ) : [];

    return links;
  }

  getMetricNumber(analysis, data) {
    if (data && (data.status === ANALYSIS_STATUS.HIGH || data.status === ANALYSIS_STATUS.LOW)) {
      switch (analysis) {
        case 'ct':
          const workMinutesPerDay = getWorkMinutesPerDay(this.props.excludeWeekendOpts, this.props.useWorkingHoursInCycleTimeDays);
          return data.cards && data.cards.length > 0 && _.isNumber(data.cards[0].cycleTimeInMinutes)
            ? Math.ceil(data.cards[0].cycleTimeInMinutes / workMinutesPerDay)
            : '';
        default:
          return '';
      }
    }

    return '';
  }

  getCtbLabelText(analysis) {
    if (analysis.status === ANALYSIS_STATUS.NORMAL && analysis.displayMetric) {
      return `${analysis.displayMetric} Cycle Time`;
    } else if (analysis.status !== ANALYSIS_STATUS.NOT_ENOUGH_DATA && analysis.displayName) {
      return `${analysis.displayName} Cycle Time`;
    }

    return 'Cicle Time';
  }

/*<div
          className="grid-stack-item"
          id={`card-${analysisType}`}
          gs-x={gridPosition.x}
          gs-y={gridPosition.y}
          gs-w={gridPosition.w}
          gs-h={gridPosition.h}
          gs-min-w={gridPosition.minW}
          key={`${analysisType}`}
        >*/

  renderAnalysisCard(analysis, analysisType, namePrefix, chart, gridPosition, style) {
    if (!analysis) {
      return (
        <div
          className="grid-stack-item"
          id={`card-${analysisType}`}
          gs-w={gridPosition ? gridPosition.w : 6}
          key={`${analysisType}`}
          style={style}
        >
          <InsightCardItem name="Loading..." customizeMode={this.props.customizeMode} />
        </div>
      );
    }

    let status = analysis.status ? analysis.status.toLowerCase() : '';
    if (analysisType == 'fe' || analysisType == 'th') {
      if (analysis.status === ANALYSIS_STATUS.HIGH || analysis.status === ANALYSIS_STATUS.HIGH_RARE) {
        status = ANALYSIS_STATUS.LOW.toLowerCase();
      }
      if (analysis.status === ANALYSIS_STATUS.LOW || analysis.status === ANALYSIS_STATUS.LOW_RARE) {
        status = ANALYSIS_STATUS.HIGH.toLowerCase();
      }
      if (analysis.status === ANALYSIS_STATUS.NORMAL || analysis.status === ANALYSIS_STATUS.NORMAL_RARE) {
        status = ANALYSIS_STATUS.NORMAL.toLowerCase();
        if (analysisType == 'th') {
          chart = 'cta';
        }
      }
    }

    const valueType = analysisType === 'wip' ? 'tasks' :
      analysisType === 'fe' ? '%' :
        analysisType === 'th' ? '' :
          'days';

    const analisisLabelText = analysisType === 'ctb' ?
      this.getCtbLabelText(analysis) :
      analysisType === 'wipAge' ? `Total ${namePrefix}`
        : namePrefix;

    return (
      <div
        className="grid-stack-item"
        id={`card-${analysisType}`}
        gs-w={gridPosition ? gridPosition.w : 6}
        key={`${analysisType}`}
        style={style}
      >
        <InsightCardItem
          className={`card-item--${status} ${analysisType}`}
          name={`${namePrefix}`}
          text={this.getText(analysisType, analysis)}
          description={this.getDescription(analysisType, analysis)}
          status={STATUS_TITLE[analysis.status]}
          analisisLabelText={analisisLabelText}
          chartValues={{
            low: analysis.lowerLimit,
            high: analysis.upperLimit,
            currentValue: analysis.displayValue
          }}
          valueType={valueType}
          chart={chart}
          dashboardId={this.props.dashboardId}
          chartName={CHART_NAMES[chart]}
          style={{ visibility: this.props[`${analysisType}AnalysisisLoading`] === false ? 'visible' : 'hidden' }}
          customizeMode={this.props.customizeMode}
        >
          {analysis.cards && analysis.cards.length > 0 && (
            <React.Fragment>{this.getTasksLinks(analysis.cards, analysisType, analysis)}</React.Fragment>
          )}
        </InsightCardItem>
      </div>
    );
  }


  render() {
    const { customizeMode, isWidgetVisible, hasFlowEfficiency } = this.props;
    //const className = !hasFlowEfficiency ? 'card-list no-fe' : 'card-list';
    const hasFE = !hasFlowEfficiency ? 6 : 4;
    const gridPositions = {
      wip: { w: 4 },
      ct: { w: 4 },
      th: { w: 4 },
      wipAge: { w: hasFE },
      ctb: { w: hasFE },
      fe: { w: 4 },
    };

    const renderCard = (analysisType, analysis, namePrefix, chart, gridPosition) => {
      const isVisible = !isWidgetVisible || isWidgetVisible(analysisType);
      const style = (customizeMode && !isVisible) ? { display: 'none' } : {};

      if (customizeMode || isVisible) {
        return this.renderAnalysisCard(analysis, analysisType, namePrefix, chart, gridPosition, style);
      }
      return null;
    };

    return (
      <React.Fragment>
        {renderCard('wip', this.props.wipAnalysis, 'Work in Progress', 'cta', gridPositions.wip)}
        {renderCard('ct', this.props.ctAnalysis, 'Cycle Time', 'ctsp', gridPositions.ct)}
        {renderCard('th', this.props.thAnalysis, 'Throughput', 'trc', gridPositions.th)}
        {renderCard('wipAge', this.props.wipAgeAnalysis, 'Work in Progress Age', 'cta', gridPositions.wipAge)}
        {renderCard('ctb', this.props.ctbAnalysis, 'Cycle Time per Process Step', 'ctb', gridPositions.ctb)}
        {this.props.hasFlowEfficiency && renderCard('fe', this.props.feAnalysis, 'Flow Efficiency', 'fe', gridPositions.fe)}
      </React.Fragment>
    );
  }
}

export default InsightCard;
